import { joinURL } from 'ufo';
import { useRouter } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';

export default function (): void {
  const router = useRouter();
  const chunkErrors = new Set<unknown>();

  const CHECK_INTERVAL = 1000 * 60 * 1; // 1 minute
  let backgroundVersionIntervalStarted = false;
  let hasPendingUpdate = false;

  function reloadAppAtPath(to: RouteLocationNormalized): void {
    const path = joinURL(import.meta.env.BASE_URL, to.fullPath);

    if (globalThis.location.pathname !== path) {
      globalThis.location.href = path;
      return;
    }

    globalThis.location.reload();
  }

  async function checkVersionMatch(): Promise<boolean> {
    try {
      const res = await fetch('/meta.json', { cache: 'no-store' });
      if (!res.ok) return true;

      const { version } = (await res.json()) as { version: string };
      console.log({
        metaVersion: version,
        appVersion: window.APP_VERSION,
      });
      return version === window.APP_VERSION;
    } catch {
      return true; // allow navigation if check fails
    }
  }

  function startBackgroundVersionCheck(): void {
    if (backgroundVersionIntervalStarted) return;
    backgroundVersionIntervalStarted = true;

    setInterval(async () => {
      const isMatch = await checkVersionMatch();
      if (!isMatch) {
        console.info('[version-check] New version detected — will reload on next navigation.');
        hasPendingUpdate = true;
      }
    }, CHECK_INTERVAL);
  }

  router.beforeEach((to, from, next) => {
    chunkErrors.clear();

    if (hasPendingUpdate) {
      console.info('[version-check] Reloading with new version...');
      reloadAppAtPath(to);
      return;
    }

    startBackgroundVersionCheck();
    next();
  });

  window.addEventListener('vite:preloadError', (event: Event) => {
    const customEvent = event as CustomEvent<{ message: string }>;
    chunkErrors.add(customEvent.detail);
  });

  router.onError((error, to) => {
    const isChunkError =
      error instanceof Error &&
      (/Loading chunk [\d]+ failed/i.test(error.message) ||
        /ChunkLoadError|Failed to fetch dynamically imported module/.test(error.message));

    if (isChunkError || chunkErrors.has(error) || hasPendingUpdate) {
      reloadAppAtPath(to);
    }
  });
}
